// ─── useVisualizeData ─────────────────────────────────────────────────────────
// Fetches time-series for each selected parameter over the active date range,
// aligns them by timestamp, and builds the matrix consumed by react-spreadsheet.
//
// Layout (matches the XD design):
//   row 1 : ['',          Site,   Site,   …]
//   row 2 : ['',          Device, Device, …]
//   row 3 : ['Datetime',  Param (Unit), …]
//   row 4+: [ datetime,   value,  value,  …]   ← one value column per parameter,
//                                                aligned on the shared Datetime.
//
// All ranges fetch RAW points (no server-side aggregation) so the user sees every
// 10-minute sample regardless of D/W/M/Y. The chart and the spreadsheet share
// this exact dataset.
// ──────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react'
import { projectsApi } from '../../../lib/projectsApi'

// Every view shows raw data — no aggregation, no bucketing. The label
// precision stays at minute granularity so each 10-minute sample is its
// own row in the grid (matches PERIOD_LABEL_PRECISION in ValidateTab).
const PERIOD_INTERVAL = { D: 'raw', W: 'raw', M: 'raw', Y: 'raw' }

const pad = (n) => String(n).padStart(2, '0')

// Format a UTC-ms timestamp shifted by the tz offset the API reports, so the
// grid shows the user's local clock time. `precision` controls the granularity
// of the label (and therefore the row-merge key): aggregated day/week/month
// views show date-only, hourly shows the hour, raw shows the minute.
function formatTs(ms, offsetSeconds = 0, precision = 'minute') {
  const d = new Date(ms + offsetSeconds * 1000)
  const date = `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`
  if (precision === 'date') return date
  if (precision === 'hour') return `${date} ${pad(d.getUTCHours())}:00`
  return `${date} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`
}

// Label granularity for each aggregation interval.
const INTERVAL_PRECISION = { raw: 'minute', hour: 'hour', day: 'date', week: 'date', month: 'date' }

const ro = (value) => ({ value, readOnly: true })

// `timezone`  — IANA tz string (e.g. 'America/New_York') the backend should
//                use to interpret the from/to dates and to convert returned
//                sample_time values back to wall-clock for display. MUST be
//                the customer's configured tz (NOT the browser default) or
//                the grid will pull a different slice of time than the chart
//                does, producing values that look "wrong" because they come
//                from a different point in the day.
// `refreshNonce` — opaque counter the caller bumps after a server-side
//                mutation (inline edit, Edit-Value modal, etc.) to force a
//                refetch — the hook's other deps don't change on those
//                events so without it the grid would show stale `value`
//                columns until the next view/period change.
export function useVisualizeData(selectedParameters = [], dateRangeGraph, activePeriod, refreshNonce = 0, timezone = null, includeHidden = false) {
  const [state, setState] = useState({ data: [], series: [], tzOffset: 0, loading: false, error: null })

  // Stable dependency key — refetch when the selected ids, range, or period change.
  const idsKey = selectedParameters.map((p) => p.id).join(',')
  const from = dateRangeGraph?.from
  const to = dateRangeGraph?.to
  // Resolved at the outer scope so it can be both used inside the effect AND
  // listed in the effect's dependency array. Falls back to browser TZ when
  // the caller hasn't supplied a customer TZ yet (e.g. before the auth
  // store has populated `user.timezone`).
  const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone

  useEffect(() => {
    let cancelled = false

    if (!selectedParameters.length) {
      setState({ data: [], series: [], loading: false, error: null })
      return
    }

    const interval = PERIOD_INTERVAL[activePeriod] || 'raw'

    setState((s) => ({ ...s, loading: true, error: null }))

    Promise.all(
      selectedParameters.map((p) =>
        projectsApi
          .fetchGraphData(p.id, { fromDate: from, toDate: to, timezone: tz, interval, agg: 'avg', includeHidden })
          .then((res) => ({ param: p, res }))
          .catch((err) => ({ param: p, error: err }))
      )
    )
      .then((results) => {
        if (cancelled) return

        const offset = results.find((r) => r.res?.tzOffsetSeconds != null)?.res?.tzOffsetSeconds || 0

        // Merge points into ONE row per display timestamp. Parameters on the
        // same logger are often recorded a few seconds apart, so keying on the
        // raw ms would split a single timestamp into several rows (each holding
        // one parameter's value). Key on the minute-precision label instead.
        const labelPrecision = INTERVAL_PRECISION[interval] || 'minute'
        const rowMap = new Map()
        results.forEach(({ res }, i) => {
          const prec = res?.precision ?? 0
          ;(res?.data || []).forEach((pt) => {
            if (pt.y === null || pt.y === undefined) return
            const label = formatTs(pt.x, offset, labelPrecision)
            let row = rowMap.get(label)
            if (!row) {
              row = { sortX: pt.x, vals: new Array(results.length).fill('') }
              rowMap.set(label, row)
            }
            if (pt.x < row.sortX) row.sortX = pt.x
            row.vals[i] = Number(pt.y).toFixed(prec)
          })
        })
        // Ascending by sample time — oldest readings at the top of the grid,
        // newest at the bottom. This matches the chart, which plots its series
        // ascending left → right, so the table reads top-to-bottom in the same
        // order the graph reads left-to-right.
        const sortedRows = [...rowMap.entries()].sort((a, b) => a[1].sortX - b[1].sortX)

        // Header rows
        const siteRow = ['', ...results.map(({ param }) => param.siteName || '')]
        const deviceRow = ['', ...results.map(({ param }) => param.deviceName || '')]
        const paramRow = [
          'Datetime',
          ...results.map(({ param, res }) => {
            const unit = res?.unit || param.unit || ''
            const name = res?.parameterName || param.name || ''
            return unit ? `${name} (${unit})` : name
          }),
        ]

        // Keep a parallel array of per-row epoch-ms so consumers (e.g.
        // ValidateTab's graph→table range filter) can compare each data
        // row against a millisecond range without re-parsing labels.
        const dataRows       = sortedRows.map(([label, row]) => [label, ...row.vals])
        const dataRowsMs     = sortedRows.map(([, row]) => row.sortX)

        const matrix = [siteRow, deviceRow, paramRow, ...dataRows].map((row, rIdx) => {
          const isDataRow = rIdx >= 3
          return row.map((v, cIdx) => {
            const cell = ro(v)
            // Stamp the row's ms onto the first (Datetime) cell so
            // downstream code can filter without re-parsing the label.
            if (isDataRow && cIdx === 0) cell.ms = dataRowsMs[rIdx - 3]
            return cell
          })
        })

        // Series for the chart popup (reuses the same fetched data).
        // `parameter_id` (the Parameterlib PID, distinct from `id`) is carried
        // through so the Wind Rose chart type can auto-detect a wind-speed /
        // wind-direction pair among the selected parameters — same PID-based
        // matching the Overview dashboard's Wind Rose widget already uses
        // (see WIND_PAIRS in DashboardWidgets.jsx).
        const series = results.map(({ param, res }) => ({
          id: param.id,
          parameter_id: param.parameter_id,
          // Param NID — chart Y-axis grouping key (same NID == same param+unit).
          param_nid: param.param_nid ?? null,
          name: res?.parameterName || param.name,
          unit: res?.unit || param.unit || '',
          siteName: param.siteName || '',
          deviceName: param.deviceName || '',
          precision: res?.precision ?? 0,
          // Keep null-y points (hidden-status gaps from graph-data) so the chart
          // line BREAKS at hidden sections; only drop malformed x. Stats helpers
          // in ChartPopup null-guard these. (The spreadsheet matrix above still
          // skips nulls — the table shouldn't show blank hidden rows.)
          points: (res?.data || []).filter((pt) => pt.x !== null && pt.x !== undefined),
        }))

        const firstError = results.find((r) => r.error)?.error
        setState({ data: matrix, series, tzOffset: offset, loading: false, error: firstError ? 'Some parameters failed to load.' : null })
      })
      .catch((err) => {
        if (!cancelled) setState({ data: [], series: [], loading: false, error: err.message || 'Failed to load data' })
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, from, to, activePeriod, refreshNonce, tz, includeHidden])

  return state
}
